import { Stack, Button, Typography, Box, LinearProgress, Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
  GetFrameRate,
  GetPlayerSetupData,
  GetRealCurrentFrame,
  SetPlayerInput,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import ReactPlayerWrapper from "../../MediaPlayer/ReactPlayerWrapper/player-wrapper";
import { DbShareMediaInfos } from "../../../Core/Api/Types/db-share-media-infos";
import { shareService } from "../../../Core/Api/services";
import { DbAssetMarkerInfos } from "../../../Core/Api/Types/db-asset-marker-infos";
import {
  ConsecutiveRanges,
  ConsecutiveRangesResult,
  ConvertSecondsToHHMMSS,
  DistinctByProp,
} from "../../../Utils/helpers";
import { MapQCMarkerData } from "../../Cards/QCMarkerCard/qc-marker-card";
import FormSection from "../../Forms/FormSection/form-section";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PushPinIcon from "@mui/icons-material/PushPin";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { IpLocationServiceApiResponse } from "../../../Core/Api/Share/share-api-client";
import { SetPlayerHeight, SetPlayerResponsive } from "../../../Redux/Reducers/redux-slice-layout";
import {
  SetActiveTimeline,
  SetFilteredTimelineMarkers,
  SetTimelineMarkers,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { useTranslation } from "react-i18next";
import { GridColDef } from "@mui/x-data-grid-premium";
import DataTable, { CellSize } from "../../Table/table";
import TableCellDate2Lines from "../../Table/TableCellDate2Lines";

export type ModalShareMetricsElementProps = {
  onClose?: (props: any) => void;
  share: DbShareMediaInfos;
};

export type ModalShareMetricsProps = CommonModalProps<ModalShareMetricsElementProps>;

const ModalShareMetrics = React.forwardRef<any, ModalShareMetricsProps>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { share, closeModal, onClose } = props;

  const [markers, setMarkers] = useState<DbAssetMarkerInfos[]>([]);
  const [totalReadSecs, setTotalReadSecs] = useState<number>(0);
  const [mapZoom, setMapZoom] = useState<number>(1);
  const [ipsCoordonates, setIpsCoordonates] = useState<IpLocationServiceApiResponse[]>([]);
  const frameRate = useAppSelector(GetFrameRate);
  const duration = useAppSelector(GetPlayerSetupData)?.durationInSeconds;

  const chunkLengthSecs = 4;
  const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  useEffect(() => {
    dispatch(SetPlayerInput({ objectId: null, episode: null, shareMediaKey: share.mediaKey }));
    dispatch(SetPlayerResponsive(true));
    dispatch(SetPlayerHeight(null));

    shareService.GetShareMetadataById(share.id).then((response) => {
      if (response.markers) {
        let distinctMarkers = DistinctByProp(response.markers, "frameIn");
        setTotalReadSecs(distinctMarkers.length * chunkLengthSecs);

        // Find consecutive frames in array
        // https://www.geeksforgeeks.org/find-all-ranges-of-consecutive-numbers-from-array/
        let allChunkIndexes: number[] = distinctMarkers.map((f) => f.id);
        let consecutiveRanges: ConsecutiveRangesResult[] = ConsecutiveRanges(allChunkIndexes).sort((a, b) =>
          a.in > b.in ? 1 : -1
        );

        // Remake a clean list of consecutive markers
        let finalMarkersList: DbAssetMarkerInfos[] = [];

        consecutiveRanges.forEach((range) => {
          let markerIn = response.markers?.find((x) => x.id === range.in);

          if (!markerIn) return;

          if (range.out !== null) {
            let markerOut = response.markers?.find((x) => x.id === range.out);
            if (markerOut) markerIn.frameOut = markerOut?.frameOut;
          }

          finalMarkersList.push(markerIn);
        });

        setMarkers(finalMarkersList);

        // Geo IP
        if (response.geoIps && response.geoIps.length > 0) {
          setIpsCoordonates(response.geoIps);
        }
      }
    });
  }, [share]);

  useEffect(() => {
    const markersCard = MapQCMarkerData(markers, frameRate);
    dispatch(SetTimelineMarkers(markersCard));
    dispatch(SetFilteredTimelineMarkers(markersCard));
    dispatch(SetActiveTimeline(TimelineType.Markers));
  }, [markers]);

  const percentRead: number = useMemo(() => {
    if (duration === 0 || totalReadSecs === 0) return 0;

    return Math.round((100 * totalReadSecs) / duration);
  }, [duration, totalReadSecs]);

  // Cols
  const columnsAccessListTable: GridColDef[] = [
    {
      field: "date",
      headerName: t("common:createdAt"),
      type: "date",
      valueGetter: (data: any, row: any) => new Date(row?.creationTime),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: any) => <TableCellDate2Lines date={data?.row?.date} />,
    },
    {
      field: "remoteIp",
      headerName: t("common:owner"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
    },
  ];

  return (
    <Modal
      title={t("modal:title.shareNameMetrics", { name: share.name })}
      reference={ref}
      closeModal={closeModal}
      width={800}
      footer={
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <>
        <FormSection icon={<PlayCircleIcon />} title={t("common:player")} defaultExpanded size="small">
          <ReactPlayerWrapper />
        </FormSection>
        {duration && (
          <FormSection
            icon={<MyLocationIcon />}
            title={t("form:section.title.playDetails")}
            defaultExpanded
            size="small"
            sx={{ mt: 2 }}
          >
            <Typography>
              {t("form:section.title.secondsRead", {
                totalReadSecs: ConvertSecondsToHHMMSS(totalReadSecs),
                duration: ConvertSecondsToHHMMSS(Math.round(duration)),
                percentRead,
              })}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" value={percentRead} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {percentRead}%
                </Typography>
              </Box>
            </Box>
          </FormSection>
        )}

        {ipsCoordonates.length > 0 && (
          <FormSection icon={<PushPinIcon />} title={t("common:geography")} defaultExpanded size="small" sx={{ mt: 2 }}>
            <ComposableMap>
              <ZoomableGroup
                center={[0, 0]}
                onMoveEnd={({ coordinates, zoom }) => {
                  setMapZoom(zoom);
                  console.log(coordinates, zoom);
                }}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: {
                            fill: "#8f70f2",
                          },
                          hover: {
                            fill: "#6c50c7",
                          },
                          pressed: {
                            fill: "#E42",
                          },
                        }}
                      />
                    ))
                  }
                </Geographies>
                {ipsCoordonates.map((coord) => (
                  <Marker coordinates={[coord.lon, coord.lat]}>
                    <Tooltip title={`${coord.city}, ${coord.country} (${coord.regionName})`}>
                      <circle r={6 / mapZoom} fill="#F53" />
                    </Tooltip>
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
          </FormSection>
        )}

        <FormSection icon={<PushPinIcon />} title={t("common:accessList")} defaultExpanded size="small" sx={{ mt: 2 }}>
          <DataTable
            noControls
            rows={share.viewInfos ?? []}
            columns={columnsAccessListTable}
            checkboxSelection={false}
            disableRowSelectionOnClick
            pagination={true}
            getRowHeight={() => "auto"}
            getRowId={() => Math.random()}
          />
        </FormSection>
      </>
    </Modal>
  );
});

export default ModalShareMetrics;
